/**
 * 자율 개체 — 모험가 (M2: 유틸리티 판단 파이프라인).
 *
 * 지시서 3.3의 파이프라인을 그대로 구현한다:
 *   욕구 갱신 → 후보 행동 나열 → 성향 가중치로 점수화 → 최고점 실행 → 이벤트 기록
 *
 * 성향(traits)은 행동을 직접 고르지 않고 후보의 점수를 기울이기만 한다.
 * 점수화 단계에는 난수가 없다(같은 상태 = 같은 판단). 난수는 전투/스폰/파티 판정에만.
 */
import {
  DESIRE,
  FRONT,
  GRADES,
  SEASON,
  PARCEL_BUILDS,
  PARTY,
  SYLLABLES,
  WEAPON,
  WORLD,
  wantWeaponTier,
  type BuildKind,
} from '@villager/shared';
import { frontZ, SOUTH, TERR_W } from './coords.js';
import { findParcelService, parcelCount, parcelServiceCells, receivePayment } from './economy.js';
import { avgTrait, rollTraits } from './traits.js';
import type { Dest, Enemy, Group, Member, ServiceKind } from './state.js';
import type { World } from './sim.js';

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

export function groupPower(g: Group): number {
  return g.members.reduce((s, a) => s + 8 + a.grade * 7 + a.weapon * 9, 0);
}

function gradeUp(w: World, a: Member): void {
  if (a.grade >= 6) return;
  if (a.kills >= (a.grade + 1) * 3) {
    a.grade++;
    a.maxhp += 22;
    a.hp = a.maxhp;
    a.kills = 0;
    w.log.emit(w.tick, 'grade_up', { advId: String(a.id) }, { ...a.pos }, { grade: a.grade });
    w.feed(`<b>${a.name}</b>이(가) <b>${GRADES[a.grade]}급</b>으로 올라섰다.`);
  }
}

/** 리더를 목표로 이동, 대원은 대형 추종. 도착(거리<2.5) 시 true. */
function walkGroup(g: Group, tx: number, tz: number, dt: number): boolean {
  const L = g.members[0]!;
  const dx = tx - L.pos.x;
  const dz = tz - L.pos.z;
  const d = Math.hypot(dx, dz);
  if (d < 2.5) return true;
  const ux = dx / d;
  const uz = dz / d;
  L.pos.x += ux * g.speed * dt;
  L.pos.z += uz * g.speed * dt;
  for (let i = 1; i < g.members.length; i++) {
    const M = g.members[i]!;
    const side = (i % 2 ? 1 : -1) * Math.ceil(i / 2);
    const bx = L.pos.x - ux * i * 1.7 + side * 1.1;
    const bz = L.pos.z - uz * i * 1.7;
    const k = Math.min(1, dt * 4);
    M.pos.x += (bx - M.pos.x) * k;
    M.pos.z += (bz - M.pos.z) * k;
  }
  return false;
}

export function newGroup(w: World, size: number): void {
  const members: Member[] = [];
  const baseX = (w.rng.next() - 0.5) * TERR_W * 0.7;
  const baseZ = SOUTH - 6;
  for (let i = 0; i < size; i++) {
    members.push({
      id: w.nextId(),
      name: w.rng.pick(SYLLABLES.A) + w.rng.pick(SYLLABLES.B),
      grade: 0,
      hp: 42,
      maxhp: 42,
      weapon: 0,
      gold: 40 + w.rng.int(35),
      hunger: w.rng.int(40),
      kills: 0,
      pos: { x: baseX + i * 1.6, z: baseZ + i * 1.2 },
      traits: rollTraits(w.rng),
      bornTick: w.tick,
    });
  }
  w.stats.spawned += size;
  const g: Group = {
    id: w.nextId(),
    members,
    dest: null,
    mode: 'roam',
    vk: null,
    wait: 0,
    speed: 6 + w.rng.next() * 2.5,
    solo: size === 1,
    target: null,
  };
  w.groups.push(g);
  const lead = members[0]!;
  if (size > 1) {
    w.log.emit(w.tick, 'party_formed', { advId: String(lead.id) }, { ...lead.pos }, { size });
    w.feed(`<b>${lead.name}</b> 일행 ${size}인이 남쪽 관문으로 들어왔다.`);
  } else {
    w.log.emit(w.tick, 'adv_spawn', { advId: String(lead.id) }, { ...lead.pos });
    w.feed(`모험가 <b>${lead.name}</b>이(가) 홀로 여정을 시작했다.`);
  }
}

function killAdv(w: World, g: Group, a: Member, killer: string): void {
  w.deadCount++;
  w.stats.lifetimeSum += w.tick - a.bornTick;
  w.stats.lifetimeCount++;
  w.stats.deathsByGrade[a.grade] = (w.stats.deathsByGrade[a.grade] ?? 0) + 1;
  const pos = { ...a.pos };
  w.tombs.push({ pos, name: a.name, grade: a.grade });
  if (a.weapon > 0) w.relics.push({ pos: { ...pos }, tier: a.weapon, owner: a.name });
  w.log.emit(w.tick, 'death', { advId: String(a.id) }, pos, { grade: a.grade, killer });
  w.feed(`🪦 <b>${a.name}</b>(${GRADES[a.grade]}급)이(가) ${killer}에게 쓰러졌다. 묘비가 세워졌다.`, true);
  g.members.splice(g.members.indexOf(a), 1);
  if (!g.members.length) w.groups.splice(w.groups.indexOf(g), 1);
  else g.solo = g.members.length === 1;
}

/** 시즌 종료 — 개인 연대기 요약 생성 (지시서 M3). */
function victory(w: World, L: Member): void {
  if (w.demonKing) w.demonKing.dead = true;
  w.seasonOver = true;

  const heroPatron = w.patrons.get(L.id);
  w.seasonResult = {
    heroName: L.name,
    heroGrade: L.grade,
    dead: w.deadCount,
    sales: w.sales,
    netProfit: w.netProfit,
    taxPaid: w.taxPaid + w.upkeepPaid,
    feesPaid: w.feesPaid,
    parcels: w.ownedTiles().length,
    tombs: w.tombs.length,
    seasonSeconds: w.time,
    topPatrons: w.topPatrons(3).map((p) => ({
      name: p.name,
      visits: p.visits,
      spent: p.spent,
      grade: p.topGrade,
    })),
    heroWasPatron: !!heroPatron,
    heroVisits: heroPatron?.visits ?? 0,
    heroSpent: heroPatron?.spent ?? 0,
  };

  w.log.emit(w.tick, 'victory', { advId: String(L.id) }, { ...L.pos }, {
    grade: L.grade,
    wasPatron: !!heroPatron,
  });
  w.feed(`<b>${L.name}</b>이(가) 마왕을 쓰러뜨리고 용사가 되었다!`);
  if (heroPatron) {
    w.feed(`그는 당신의 가게를 ${heroPatron.visits}번 찾았던 <b>단골</b>이었다.`);
  }
}

function killMob(w: World, g: Group, m: Enemy): void {
  const L = g.members[0]!;
  if (m.king) {
    // 끝없는 세계: 마왕은 쓰러지지 않는다. 빈사에서 되살아나 세계가 계속된다.
    if (SEASON.ENDLESS) {
      m.hp = m.maxhp * SEASON.KING_REVIVE_RATIO;
      w.log.emit(w.tick, 'kill', { advId: String(L.id), enemyId: String(m.id) }, { ...m.pos }, {
        kingSurvived: true,
      });
      w.feed(`<b>${L.name}</b> 일행이 마왕을 몰아붙였지만, 마왕은 다시 일어섰다.`, true);
      return;
    }
    victory(w, L);
    return;
  }
  m.dead = true;
  for (const a of g.members) {
    a.gold += m.boss ? 60 : m.named ? 22 : 8;
    a.kills += m.boss ? 6 : m.named ? 3 : 1;
    gradeUp(w, a);
  }
  if (m.boss) {
    w.stats.bossKills++;
    w.log.emit(w.tick, 'boss_kill', { advId: String(L.id), enemyId: String(m.id) }, { ...m.pos }, { boss: m.name });
    w.feed(`⚔ <b>${L.name}</b> 일행이 사천왕 <b>${m.name}</b>을(를) 쓰러뜨렸다! 전선이 크게 밀려 올라간다.`);
    w.frontRow = Math.max(1, w.frontRow - 1);
    w.frontDirty = true;
    if (w.bosses.every((b) => b.dead)) {
      w.feed('사천왕이 모두 쓰러졌다. <b>마왕성의 문이 열린다.</b>', true);
    }
  } else {
    const idx = w.mobs.indexOf(m);
    if (idx >= 0) w.mobs.splice(idx, 1);
    w.pushKills++;
    if (m.named) w.stats.namedKills++;
    else w.stats.mobKills++;
    w.log.emit(w.tick, 'kill', { advId: String(L.id), enemyId: String(m.id) }, { ...m.pos });
    if (w.pushKills >= FRONT.PUSH_KILLS_PER_ROW) {
      w.pushKills = 0;
      if (w.frontRow > 1) {
        w.frontRow--;
        w.frontDirty = true;
        w.log.emit(w.tick, 'front_advance', {}, undefined, { frontRow: w.frontRow });
        w.feed('전선이 북쪽으로 전진했다. 새 개척지가 열렸다.');
      }
    }
  }
}

// ─────────────────────────────────────────────
// 판단 파이프라인
// ─────────────────────────────────────────────

type Action =
  | { kind: 'visit'; vk: ServiceKind; dest: Dest; score: number }
  | { kind: 'hunt'; target: Enemy; score: number }
  | { kind: 'wander'; score: number };

/** 서비스 필지 캐시 갱신 (틱당 1회 — 그룹마다 그리드를 스캔하지 않기 위해). */
function refreshServices(w: World): void {
  w.services.inn = findParcelService(w, 'inn', true);
  w.services.eat = findParcelService(w, 'eat', true);
  w.services.shop = findParcelService(w, 'shop', true);
  w.services.train = findParcelService(w, 'train', false);
}

/**
 * 후보 행동 나열 + 성향 가중 점수화 → 최고점 반환.
 * 난수 없음: 같은 상태면 같은 결정.
 */
function decide(w: World, g: Group): Action {
  const L = g.members[0]!;
  const t = L.traits;
  const power = groupPower(g);
  const cands: Action[] = [];

  // 1) 회복 욕구 → 여관
  const hpRatio = L.hp / L.maxhp;
  const restNeed = clamp01((DESIRE.REST_THRESHOLD - hpRatio) / DESIRE.REST_THRESHOLD);
  if (restNeed > 0 && w.services.inn) {
    cands.push({
      kind: 'visit',
      vk: 'inn',
      dest: w.services.inn,
      score: restNeed * DESIRE.REST_BASE * (0.65 + 0.7 * t.caution),
    });
  }

  // 2) 허기 → 밭
  const eatNeed = clamp01((L.hunger - DESIRE.EAT_THRESHOLD) / (DESIRE.EAT_MAX - DESIRE.EAT_THRESHOLD));
  if (eatNeed > 0 && w.services.eat) {
    cands.push({ kind: 'visit', vk: 'eat', dest: w.services.eat, score: eatNeed * DESIRE.EAT_BASE });
  }

  // 3) 장비 갈망 → 대장간 (등급별 티어 수요 차등)
  const want = wantWeaponTier(L.grade);
  if (L.weapon < want && w.services.shop) {
    const price = WEAPON.PRICE_PER_TIER * (L.weapon + 1);
    if (L.gold >= price) {
      const gearNeed = (want - L.weapon) / want;
      // 탐욕이 높을수록 지출을 주저한다
      cands.push({
        kind: 'visit',
        vk: 'shop',
        dest: w.services.shop,
        score: gearNeed * DESIRE.GEAR_BASE * (1.25 - 0.5 * t.greed),
      });
    }
  }

  // 4) 성장 욕구 → 던전 훈련 (충동적일수록 위험한 훈련을 감수)
  if (w.services.train && L.gold >= 20 && L.grade < 4) {
    const growth = (4 - L.grade) / 4;
    cands.push({
      kind: 'visit',
      vk: 'train',
      dest: w.services.train,
      score: growth * DESIRE.TRAIN_BASE * (0.6 + 0.8 * (1 - t.caution)),
    });
  }

  // 5)/6) 사냥 — 가장 가까운 잡몹 / 도전 가능한 보스
  const fz = frontZ(w.frontRow);
  let nearMob: Enemy | null = null;
  let mobD = Infinity;
  for (const m of w.mobs) {
    const d = Math.hypot(m.pos.x - L.pos.x, m.pos.z - L.pos.z);
    if (d < mobD) {
      mobD = d;
      nearMob = m;
    }
  }
  let nearBoss: Enemy | null = null;
  let bossD = Infinity;
  for (const b of w.bosses) {
    if (b.dead || b.pos.z < fz - WORLD.TILE * 1.5) continue;
    const d = Math.hypot(b.pos.x - L.pos.x, b.pos.z - L.pos.z);
    if (d < bossD) {
      bossD = d;
      nearBoss = b;
    }
  }
  if (!nearBoss && w.bosses.every((b) => b.dead) && w.demonKing && !w.demonKing.dead) {
    nearBoss = w.demonKing;
    bossD = Math.hypot(w.demonKing.pos.x - L.pos.x, w.demonKing.pos.z - L.pos.z);
  }

  // 자금 욕구: 부족하면 (탐욕할수록 더) 저위험 사냥을 선호
  const fundsNeed = clamp01((DESIRE.FUNDS_LOW - L.gold) / DESIRE.FUNDS_LOW);

  if (nearMob) {
    const risk = nearMob.pow / (power + 1);
    const stick = g.target === nearMob ? 0.08 : 0; // 목표 흔들림 방지
    cands.push({
      kind: 'hunt',
      target: nearMob,
      score:
        DESIRE.HUNT_BASE +
        fundsNeed * DESIRE.FUNDS_BASE * (0.5 + t.greed) -
        risk * DESIRE.RISK_PENALTY * t.caution -
        mobD / DESIRE.HUNT_DIST_DIV +
        stick,
    });
  }
  if (nearBoss) {
    const risk = nearBoss.pow / (power + 1);
    const stick = g.target === nearBoss ? 0.08 : 0;
    // 공명심이 도전을 끌어올리고, "준비됨(전투력 충분)"이 실제 도전을 성립시킨다.
    // 이 항이 없으면 보스 점수가 잡몹을 절대 못 넘어 시즌이 끝나지 않는다.
    const ready = clamp01(power / (nearBoss.pow * DESIRE.BOSS_READY_SCALE));
    cands.push({
      kind: 'hunt',
      target: nearBoss,
      score:
        DESIRE.BOSS_BASE * (0.35 + 1.3 * t.glory) +
        DESIRE.BOSS_READY_BONUS * ready -
        risk * DESIRE.RISK_PENALTY * t.caution -
        bossD / DESIRE.BOSS_DIST_DIV +
        stick,
    });
  }

  cands.push({ kind: 'wander', score: DESIRE.WANDER });

  let best = cands[0]!;
  for (const c of cands) if (c.score > best.score) best = c;
  return best;
}

/** 방문 도착 시 서비스 처리(거래/훈련). */
function serveAtParcel(w: World, g: Group, dest: Dest): void {
  const parcel = w.grid[dest.c]?.[dest.r]?.parcel ?? null;
  if (parcel && parcel.paralyzed <= 0) {
    const owner = parcel.owner; // 매출·단골은 필지 주인에게 귀속
    if (g.vk === 'train') {
      const fee = 12 + parcelCount(parcel, 'chest') * 8;
      const torch = parcelCount(parcel, 'torch');
      const denBonus = 1 + Math.min(2, Math.floor(parcelCount(parcel, 'den') / 2));
      for (const a of g.members.slice()) {
        if (a.gold < fee) continue;
        a.gold -= fee;
        receivePayment(w, fee, owner);
        w.recordPatron(owner, a.id, a.name, a.grade, fee);
        a.kills += denBonus;
        if (w.rng.chance(Math.max(0.02, 0.09 - torch * 0.02))) {
          killAdv(w, g, a, '당신의 던전');
          w.feed('입장료는 이미 받았다.', true);
        } else {
          gradeUp(w, a);
        }
      }
    } else if (g.vk) {
      for (const a of g.members) {
        const cells = parcelServiceCells(parcel, g.vk as BuildKind, true);
        if (!cells.length) break;
        const cell = cells[0]!;
        if (g.vk === 'shop') {
          // 등급별 수요 차등: 이미 자기 등급이 원하는 티어를 채웠으면 사지 않는다
          if (a.weapon >= wantWeaponTier(a.grade)) continue;
          const tier = a.weapon + 1;
          const price = WEAPON.PRICE_PER_TIER * tier;
          if (a.gold < price) continue;
          a.gold -= price;
          receivePayment(w, price, owner);
          w.recordPatron(owner, a.id, a.name, a.grade, price);
          cell.stock--;
          a.weapon = tier;
          w.stats.purchases++;
          w.log.emit(w.tick, 'trade', { advId: String(a.id), parcelId: `${dest.c},${dest.r}`, itemTier: tier }, undefined, { price, tier });
          w.feed(`<b>${a.name}</b>이(가) 당신의 대장간에서 ${tier}단 무기를 샀다. (+${price}금)`);
        } else {
          const price = g.vk === 'eat' ? PARCEL_BUILDS.village.farm.price : PARCEL_BUILDS.village.inn.price;
          if (a.gold < price) continue;
          a.gold -= price;
          receivePayment(w, price, owner);
          w.recordPatron(owner, a.id, a.name, a.grade, price);
          cell.stock--;
          if (g.vk === 'eat') a.hunger = 0;
          else a.hp = a.maxhp;
        }
      }
    }
  }
  w.markParcelDirty(dest.c, dest.r);
}

// ─────────────────────────────────────────────
// 파티 동학 — 결성 / 리더 교대 / 해산
// ─────────────────────────────────────────────

function partyDynamics(w: World, dt: number): void {
  // 리더 교대: 확실히 더 강한 멤버가 있으면 확률적으로 교대
  for (const g of w.groups) {
    if (g.members.length < 2) continue;
    let bestIdx = 0;
    for (let i = 1; i < g.members.length; i++) {
      if (g.members[i]!.grade > g.members[bestIdx]!.grade) bestIdx = i;
    }
    if (bestIdx === 0) continue;
    const L = g.members[0]!;
    const cand = g.members[bestIdx]!;
    if (cand.grade - L.grade < PARTY.LEADER_SWAP_MARGIN) continue;
    if (!w.rng.chance(PARTY.LEADER_SWAP_CHANCE * dt)) continue;
    g.members[0] = cand;
    g.members[bestIdx] = L;
    w.stats.leaderChanges++;
    w.log.emit(w.tick, 'leader_change', { advId: String(cand.id) }, { ...cand.pos }, { from: L.name });
    w.feed(`<b>${cand.name}</b>이(가) <b>${L.name}</b>을(를) 대신해 일행을 이끈다.`);
  }

  // 해산: 의리가 낮고 인원이 많을수록 잘 흩어진다
  for (const g of w.groups.slice()) {
    if (g.members.length < 2) continue;
    const loyalty = avgTrait(g.members, 'loyalty');
    const p = PARTY.DISBAND_BASE * (1 - loyalty) * g.members.length * dt;
    if (!w.rng.chance(p)) continue;
    const members = g.members.slice();
    w.groups.splice(w.groups.indexOf(g), 1);
    for (const m of members) {
      w.groups.push({
        id: w.nextId(),
        members: [m],
        dest: null,
        mode: 'roam',
        vk: null,
        wait: 0,
        speed: 6 + w.rng.next() * 2.5,
        solo: true,
        target: null,
      });
    }
    w.stats.disbands++;
    const head = members[0]!;
    w.log.emit(w.tick, 'party_disband', { advId: String(head.id) }, { ...head.pos }, { size: members.length });
    w.feed(`<b>${head.name}</b> 일행이 뜻이 갈려 <b>해산</b>했다.`);
  }

  // 솔로 조우 병합 (한 틱에 최대 1건)
  for (let a = 0; a < w.groups.length; a++) {
    for (let b = a + 1; b < w.groups.length; b++) {
      const A = w.groups[a]!;
      const B = w.groups[b]!;
      if (!A.solo || !B.solo || !A.members.length || !B.members.length) continue;
      const la = A.members[0]!;
      const lb = B.members[0]!;
      if (Math.hypot(la.pos.x - lb.pos.x, la.pos.z - lb.pos.z) >= PARTY.MERGE_DIST) continue;
      // 의리가 높을수록 잘 뭉친다
      const bond = 0.5 + 0.5 * ((la.traits.loyalty + lb.traits.loyalty) / 2);
      if (!w.rng.chance(PARTY.MERGE_CHANCE * bond * dt)) continue;
      A.members.push(...B.members);
      A.solo = false;
      B.members.length = 0;
      w.groups.splice(b, 1);
      w.stats.merges++;
      w.log.emit(w.tick, 'party_formed', { advId: String(A.members[0]!.id) }, { ...la.pos }, { merged: true });
      w.feed(`⚔ <b>${A.members[0]!.name}</b>와(과) <b>${A.members[1]!.name}</b>이(가) 길 위에서 만나 <b>파티를 결성</b>했다!`);
      return;
    }
  }
}

// ─────────────────────────────────────────────
// 메인 갱신
// ─────────────────────────────────────────────

export function updateGroups(w: World, dt: number): void {
  refreshServices(w);

  for (const g of w.groups.slice()) {
    if (!g.members.length) continue;
    const L = g.members[0]!;

    // 유품 발굴
    for (const rr of w.relics.slice()) {
      if (Math.hypot(rr.pos.x - L.pos.x, rr.pos.z - L.pos.z) < 3 && rr.tier > L.weapon) {
        L.weapon = rr.tier;
        w.relics.splice(w.relics.indexOf(rr), 1);
        w.log.emit(w.tick, 'relic_found', { advId: String(L.id), itemTier: rr.tier }, { ...L.pos });
        w.feed(`<b>${L.name}</b>이(가) <b>${rr.owner}</b>의 묘비 곁에서 유품 무기(${rr.tier}단)를 발굴했다.`);
      }
    }

    if (g.wait > 0) {
      g.wait -= dt;
      continue;
    }

    // ── 욕구 갱신 ──
    L.hunger += dt * 2.2;

    // ── 진행 중인 방문은 도착까지 유지 (판단 흔들림 방지) ──
    if (g.mode === 'visit' && g.dest) {
      if (walkGroup(g, g.dest.x, g.dest.z, dt)) {
        serveAtParcel(w, g, g.dest);
        g.mode = 'roam';
        g.dest = null;
        g.vk = null;
        g.wait = 1;
      }
      continue;
    }

    // ── 후보 나열 → 점수화 → 최고점 실행 ──
    const act = decide(w, g);
    if (act.kind === 'visit') {
      g.mode = 'visit';
      g.vk = act.vk;
      g.dest = act.dest;
      g.target = null;
      continue;
    }
    if (act.kind === 'hunt') {
      g.mode = 'hunt';
      g.target = act.target;
      if (walkGroup(g, act.target.pos.x, act.target.pos.z, dt)) {
        const m = act.target;
        const gp = groupPower(g);
        m.hp -= gp * dt * 3 * (0.7 + w.rng.next() * 0.6);
        const n = g.members.length;
        for (const a of g.members) a.hp -= (m.pow * dt * 1.2 * (0.7 + w.rng.next() * 0.6)) / n;
        const dead = g.members.find((a) => a.hp <= 0);
        if (dead) killAdv(w, g, dead, m.name);
        if (m.hp <= 0 && g.members.length) killMob(w, g, m);
      }
      continue;
    }
    // 배회
    g.mode = 'roam';
    g.target = null;
    if (!g.dest || w.rng.chance(0.005)) {
      g.dest = {
        x: (w.rng.next() - 0.5) * TERR_W * 0.8,
        z: frontZ(w.frontRow) + 8 + w.rng.next() * 70,
        c: -1,
        r: -1,
      };
    }
    if (walkGroup(g, g.dest.x, g.dest.z, dt)) g.dest = null;
  }

  partyDynamics(w, dt);
}
